const CAPTURE_SHORTCUTS = [
    (event) => {
        const key = String(event.key || '').toLowerCase();
        const code = String(event.code || '');
        if (key === 'printscreen' || code === 'PrintScreen') return true;
        if (event.metaKey && event.shiftKey && ['3', '4', '5', 's'].includes(key)) return true;
        if (event.ctrlKey && event.shiftKey && key === 's') return true;
        if (event.altKey && (key === 'printscreen' || code === 'PrintScreen')) return true;
        return false;
    },
    (event) => {
        const key = String(event.key || '').toLowerCase();
        if (key === 'f12') return true;
        if (event.ctrlKey && event.shiftKey && ['i', 'j', 'c', 'k'].includes(key)) return true;
        if (event.ctrlKey && ['u', 's', 'p'].includes(key)) return true;
        if (event.metaKey && event.altKey && key === 'i') return true;
        return false;
    },
];

export function isCaptureShortcut(event) {
    return CAPTURE_SHORTCUTS.some((check) => check(event));
}

let activeGuards = 0;
let restoreDisplayMedia = null;
let restoreVideoCaptureStream = null;
let globalOnViolation = null;

function handleViolation(reason = 'capture') {
    globalOnViolation?.(reason);
}

function onVisibilityChange() {
    if (document.hidden) handleViolation('hidden');
}

function onBlur() {
    handleViolation('blur');
}

function onBeforePrint(event) {
    event?.preventDefault?.();
    handleViolation('print');
}

function onCopy(event) {
    event.preventDefault();
    handleViolation('copy');
}

function onKeyDown(event) {
    if (isCaptureShortcut(event)) {
        event.preventDefault();
        event.stopPropagation();
        handleViolation('shortcut');
        try {
            navigator.clipboard?.writeText('');
        } catch {
            /* ignore */
        }
    }
}

function onContextMenu(event) {
    event.preventDefault();
}

function patchDisplayMedia() {
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.getDisplayMedia || restoreDisplayMedia) return;

    const original = mediaDevices.getDisplayMedia.bind(mediaDevices);
    restoreDisplayMedia = () => {
        mediaDevices.getDisplayMedia = original;
        restoreDisplayMedia = null;
    };

    mediaDevices.getDisplayMedia = async () => {
        handleViolation('display-media');
        throw new DOMException('Screen capture is not allowed on protected content.', 'NotAllowedError');
    };
}

function patchVideoCaptureStream() {
    if (restoreVideoCaptureStream || typeof HTMLVideoElement === 'undefined') return;
    const proto = HTMLVideoElement.prototype;
    if (typeof proto.captureStream !== 'function' && typeof proto.mozCaptureStream !== 'function') return;

    const original = proto.captureStream?.bind(proto);
    const originalMoz = proto.mozCaptureStream?.bind(proto);

    if (original) {
        proto.captureStream = function captureStreamBlocked() {
            handleViolation('video-capture-stream');
            throw new DOMException('Video capture is not allowed.', 'NotAllowedError');
        };
    }
    if (originalMoz) {
        proto.mozCaptureStream = function mozCaptureStreamBlocked() {
            handleViolation('video-capture-stream');
            throw new DOMException('Video capture is not allowed.', 'NotAllowedError');
        };
    }

    restoreVideoCaptureStream = () => {
        if (original) proto.captureStream = original;
        if (originalMoz) proto.mozCaptureStream = originalMoz;
        restoreVideoCaptureStream = null;
    };
}

export function installContentProtection({ onViolation } = {}) {
    activeGuards += 1;
    globalOnViolation = onViolation || globalOnViolation;

    if (activeGuards === 1) {
        document.body.classList.add('edulearn-content-protected');
        patchDisplayMedia();
        patchVideoCaptureStream();

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('blur', onBlur);
        window.addEventListener('beforeprint', onBeforePrint);
        document.addEventListener('copy', onCopy, true);
        document.addEventListener('cut', onCopy, true);
        document.addEventListener('contextmenu', onContextMenu, true);
        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('keyup', onKeyDown, true);
    }

    return () => uninstallContentProtection();
}

export function uninstallContentProtection() {
    if (activeGuards <= 0) return;
    activeGuards -= 1;

    if (activeGuards === 0) {
        document.body.classList.remove('edulearn-content-protected');
        restoreDisplayMedia?.();
        restoreVideoCaptureStream?.();
        globalOnViolation = null;

        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('blur', onBlur);
        window.removeEventListener('beforeprint', onBeforePrint);
        document.removeEventListener('copy', onCopy, true);
        document.removeEventListener('cut', onCopy, true);
        document.removeEventListener('contextmenu', onContextMenu, true);
        document.removeEventListener('keydown', onKeyDown, true);
        document.removeEventListener('keyup', onKeyDown, true);
    }
}

export function paintBlackCanvas(canvas) {
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
}
